<template>
  <HrPageLayout
    title="Imprest Reconciliation"
    subtitle="Manage imprest reconciliation"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Imprest' },
      { label: 'Reconciliation' }
    ]"
  >
    <div class="imprest-recon-content">
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p>Loading...</p>
      </div>

      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <p class="hr-metric-label">Total Imprest Issued</p>
          <h2 class="hr-metric-value">{{ metrics.totalImprestIssued }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Pending Reconciliation</p>
          <h2 class="hr-metric-value">{{ metrics.pendingReconciliation }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">Total Outstanding</p>
          <h2 class="hr-metric-value">{{ formatPrice(metrics.totalOutstandingAmount) }}</h2>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">Imprest Reconciliation List</h5>
          <div class="hr-flex hr-gap-2">
            <div class="hr-search-wrap">
              <input type="text" class="hr-input" v-model="search" placeholder="Search..." @input="handleSearch">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <button @click="showReconciliationModal" class="hr-btn hr-btn-primary">
              <i class="bx bx-plus"></i> New Reconciliation
            </button>
          </div>
        </div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>Imprest ID</th>
                <th>Purpose</th>
                <th>Requested By</th>
                <th>Department</th>
                <th>Imprest Amount</th>
                <th>Actual Expense</th>
                <th>Variance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in reconciliations" :key="item.request_id">
                <td>{{ item.request_id }}</td>
                <td>{{ item.purpose }}</td>
                <td>{{ item.names }}</td>
                <td>{{ item.department }}</td>
                <td>{{ formatPrice(item.amount) }}</td>
                <td>{{ formatPrice(item.actual_expense) }}</td>
                <td :class="getVarianceClass(item)">
                  {{ formatPrice((item.amount || item.imprest_amount || 0) - (item.actual_expense || 0)) }}
                </td>
                <td><span :class="getStatusBadgeClass(item.status)">{{ item.status }}</span></td>
                <td>
                  <div class="hr-btn-group">
                    <button @click="viewReconciliationDetails(item)" class="hr-btn hr-btn-sm hr-btn-info">View</button>
                    <button v-if="item.status.toLowerCase() === 'pending'" @click="submitReconciliation(item)" class="hr-btn hr-btn-sm hr-btn-success">Submit</button>
                  </div>
                </td>
              </tr>
              <tr v-if="reconciliations.length === 0">
                <td colspan="9" class="hr-text-center">No reconciliation records found</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

            <!-- Reconciliation Modal -->
            <div class="modal fade" id="reconciliationModal" tabindex="-1">
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">New Imprest Reconciliation</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                  </div>
                  <div class="modal-body">
                    <form @submit.prevent="saveReconciliation">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Imprest Request</label>
                          <select
                              class="form-select"
                              v-model="newReconciliation.imprest_id"
                              required
                          >
                            <option value="">Select Imprest Request</option>
                            <option
                                v-for="request in unreconcyledRequests"
                                :key="request.request_id"
                                :value="request.request_id"
                            >
                              {{ request.names }} - {{ request.purpose }} - {{ formatPrice(request.amount) }} - {{ formatDate(request.created_at) }} - {{ request.department }}
                            </option>
                          </select>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Actual Expense Amount</label>
                          <input
                              type="number"
                              class="form-control"
                              v-model="newReconciliation.actual_expense"
                              required
                              min="0"
                          >
                        </div>
                        <div class="col-12 mb-3">
                          <label class="form-label">Expense Details</label>
                          <textarea
                              class="form-control"
                              v-model="newReconciliation.details"
                              rows="4"
                              placeholder="Provide detailed breakdown of expensese e.g KES 1000 for food, KES 1000 for rent, KES 1000 for utilities, KES 1000 for other expenses"
                          ></textarea>
                        </div>

                        <div class="col-12 mb-3">
                          <label class="form-label">Attach Supporting Documents (PDF only)</label>
                          <input
                              type="file"
                              class="form-control"
                              multiple
                              accept=".pdf,application/pdf"
                              @change="handleFileUpload"
                          >
                          <small class="form-text text-muted">Only PDF files are allowed</small>
                        </div>

                      </div>
                      <button type="submit" class="btn btn-primary" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Save Reconciliation
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Modal -->
            <!-- Enhanced Details Modal -->
            <div class="modal fade" id="detailsModal" tabindex="-1" aria-labelledby="detailsModalLabel" aria-hidden="true">
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <div class="modal-header bg-primary text-white">
                    <h5 class="modal-title text-white" id="detailsModalLabel">
                      <i class="mdi mdi-file-document-outline me-1"></i>
                      Imprest Reconciliation Details
                    </h5>
                    <button type="button" class="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div class="modal-body" v-if="selectedReconciliation">
                    <!-- Summary Section -->
                    <div class="card mb-3 border-0 bg-light">
                      <div class="card-body p-3">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">
                            <span class="badge bg-primary me-2">ID: {{ selectedReconciliation.request_id }}</span>
                            {{ selectedReconciliation.purpose }}
                          </h5>
                          <span :class="getStatusBadgeClass(selectedReconciliation.status)">
                {{ selectedReconciliation.status }}
              </span>
                        </div>
                        <div class="row g-2">
                          <div class="col-md-6">
                            <small class="text-muted">Requested By:</small>
                            <p class="mb-0"><strong>{{ selectedReconciliation.names }}</strong></p>
                          </div>
                          <div class="col-md-6">
                            <small class="text-muted">Department:</small>
                            <p class="mb-0"><strong>{{ selectedReconciliation.department }}</strong></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Financial Information -->
                    <div class="card mb-3">
                      <div class="card-header bg-white">
                        <h6 class="mb-0"><i class="mdi mdi-cash-multiple me-1"></i> Financial Details</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-4 mb-3">
                            <div class="border rounded p-3 h-100">
                              <small class="text-muted d-block mb-1">Imprest Amount:</small>
                              <h4 class="mb-0 text-primary">{{ formatPrice(selectedReconciliation.amount) }}</h4>
                              <small class="text-muted">Approved: {{ formatDate(selectedReconciliation.approval_date) }}</small>
                            </div>
                          </div>
                          <div class="col-md-4 mb-3">
                            <div class="border rounded p-3 h-100">
                              <small class="text-muted d-block mb-1">Actual Expense:</small>
                              <h4 class="mb-0 text-info">{{ formatPrice(selectedReconciliation.actual_expense) }}</h4>
                              <small class="text-muted">Reconciled: {{ formatDate(selectedReconciliation.reconciliation_date) }}</small>
                            </div>
                          </div>
                          <div class="col-md-4 mb-3">
                            <div class="border rounded p-3 h-100">
                              <small class="text-muted d-block mb-1">Variance:</small>
                              <h4
                                  :class="[
                      'mb-0',
                      selectedReconciliation.amount - selectedReconciliation.actual_expense > 0
                        ? 'text-success'
                        : selectedReconciliation.amount - selectedReconciliation.actual_expense < 0
                          ? 'text-danger'
                          : 'text-dark'
                    ]"
                              >
                                {{ formatPrice(selectedReconciliation.amount - selectedReconciliation.actual_expense) }}
                              </h4>
                              <small class="text-muted">
                    <span v-if="selectedReconciliation.amount - selectedReconciliation.actual_expense > 0">
                      <i class="mdi mdi-arrow-down-bold text-success"></i> Refund Due
                    </span>
                                <span v-else-if="selectedReconciliation.amount - selectedReconciliation.actual_expense < 0">
                      <i class="mdi mdi-arrow-up-bold text-danger"></i> Reimbursement Required
                    </span>
                                <span v-else>
                      <i class="mdi mdi-check-circle text-dark"></i> Balanced
                    </span>
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Timeline and Transaction History -->
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="card h-100">
                          <div class="card-header bg-white">
                            <h6 class="mb-0"><i class="mdi mdi-timeline me-1"></i> Timeline</h6>
                          </div>
                          <div class="card-body p-0">
                            <ul class="list-group list-group-flush">
                              <li class="list-group-item">
                                <div class="d-flex">
                                  <div class="avatar-xs me-3">
                        <span class="avatar-title rounded-circle bg-soft-primary text-primary">
                          <i class="mdi mdi-calendar-plus"></i>
                        </span>
                                  </div>
                                  <div>
                                    <h6 class="mb-1">Request Created</h6>
                                    <p class="text-muted mb-0">{{ formatDateTime(selectedReconciliation.request_date) }}</p>
                                  </div>
                                </div>
                              </li>
                              <li class="list-group-item">
                                <div class="d-flex">
                                  <div class="avatar-xs me-3">
                        <span class="avatar-title rounded-circle bg-soft-success text-success">
                          <i class="mdi mdi-check-circle"></i>
                        </span>
                                  </div>
                                  <div>
                                    <h6 class="mb-1">Imprest Approved</h6>
                                    <p class="text-muted mb-0">{{ formatDateTime(selectedReconciliation.approval_date) }}</p>
                                    <small v-if="selectedReconciliation.notes" class="text-muted">
                                      <i class="mdi mdi-comment-text-outline me-1"></i> {{ selectedReconciliation.notes }}
                                    </small>
                                  </div>
                                </div>
                              </li>
                              <li v-if="selectedReconciliation.reconciliation_date" class="list-group-item">
                                <div class="d-flex">
                                  <div class="avatar-xs me-3">
                        <span class="avatar-title rounded-circle bg-soft-info text-info">
                          <i class="mdi mdi-file-document-edit"></i>
                        </span>
                                  </div>
                                  <div>
                                    <h6 class="mb-1">Reconciliation Submitted</h6>
                                    <p class="text-muted mb-0">{{ formatDateTime(selectedReconciliation.reconciliation_date) }}</p>
                                    <small class="text-muted">By: {{ selectedReconciliation.reconciled_by }}</small>
                                  </div>
                                </div>
                              </li>
                              <li v-if="selectedReconciliation.rejection_date" class="list-group-item">
                                <div class="d-flex">
                                  <div class="avatar-xs me-3">
                        <span class="avatar-title rounded-circle bg-soft-danger text-danger">
                          <i class="mdi mdi-close-circle"></i>
                        </span>
                                  </div>
                                  <div>
                                    <h6 class="mb-1">Reconciliation Rejected</h6>
                                    <p class="text-muted mb-0">{{ formatDateTime(selectedReconciliation.rejection_date) }}</p>
                                    <small v-if="selectedReconciliation.rejection_reason" class="text-danger">
                                      <i class="mdi mdi-alert-circle me-1"></i> {{ selectedReconciliation.rejection_reason }}
                                    </small>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6 mb-3">
                        <div class="card h-100">
                          <div class="card-header bg-white">
                            <h6 class="mb-0"><i class="mdi mdi-cash-register me-1"></i> Expense Breakdown</h6>
                          </div>
                          <div class="card-body">
                            <div v-if="selectedReconciliation.reconciliation_details">
                              <div class="expense-details p-2 rounded bg-light mb-3">
                                <pre class="mb-0" style="white-space: pre-wrap; font-family: inherit;">{{ selectedReconciliation.reconciliation_details }}</pre>
                              </div>

                              <!-- Visual breakdown if details can be parsed -->
                              <div v-if="parseExpenseDetails(selectedReconciliation.reconciliation_details).length > 0" class="mt-3">
                                <h6 class="text-muted mb-2">Visual Breakdown</h6>
                                <div class="progress" style="height: 25px;">
                                  <div v-for="(item, index) in parseExpenseDetails(selectedReconciliation.reconciliation_details)"
                                       :key="index"
                                       class="progress-bar"
                                       :class="getProgressBarColor(index)"
                                       :style="`width: ${getPercentage(item.amount, selectedReconciliation.actual_expense)}%`"
                                       :title="`${item.description}: ${formatPrice(item.amount)}`">
                                    {{ item.description }}
                                  </div>
                                </div>
                                <div class="row mt-2">
                                  <div v-for="(item, index) in parseExpenseDetails(selectedReconciliation.reconciliation_details)"
                                       :key="`legend-${index}`"
                                       class="col-md-6 mb-1">
                                    <small>
                                      <i class="mdi mdi-square" :class="getTextColor(index)"></i>
                                      {{ item.description }}: {{ formatPrice(item.amount) }}
                                      ({{ getPercentage(item.amount, selectedReconciliation.actual_expense) }}%)
                                    </small>
                                  </div>
                                </div>
                              </div>

                            </div>
                            <div v-else class="text-center text-muted p-3">
                              <i class="mdi mdi-text-box-outline" style="font-size: 3rem;"></i>
                              <p>No expense details provided</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Supporting Documents -->
                    <div class="card mb-3" v-if="selectedReconciliation.attachments && selectedReconciliation.attachments.length">
                      <div class="card-header bg-white">
                        <h6 class="mb-0"><i class="mdi mdi-attachment me-1"></i> Supporting Documents</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div v-for="(attachment, index) in selectedReconciliation.attachments" :key="index" class="col-md-6 mb-3">
                            <div class="border rounded p-3 d-flex align-items-center">
                              <div class="me-3">
                                <i class="mdi mdi-file-pdf-box text-danger" style="font-size: 2.5rem;"></i>
                              </div>
                              <div class="flex-grow-1 overflow-hidden">
                                <h6 class="text-truncate mb-1" :title="getFileName(attachment)">
                                  {{ getFileName(attachment) }}
                                </h6>
                                <div class="d-grid gap-2 d-md-flex justify-content-md-start">
                                  <a :href="getPathUrl(attachment)" target="_blank" class="btn btn-sm btn-outline-primary">
                                    <i class="mdi mdi-eye me-1"></i> View
                                  </a>
                                  <a :href="getPathUrl(attachment)" download class="btn btn-sm btn-outline-success">
                                    <i class="mdi mdi-download me-1"></i> Download
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Additional Actions Section -->
                    <div class="card mb-3" v-if="selectedReconciliation.status.toLowerCase() === 'pending'">
                      <div class="card-header bg-white">
                        <h6 class="mb-0"><i class="mdi mdi-cog me-1"></i> Actions</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-6 mb-2">
                            <button @click="submitReconciliation(selectedReconciliation)" class="btn btn-success w-100">
                              <i class="mdi mdi-check-circle me-1"></i> Submit Reconciliation
                            </button>
                          </div>
                          <div class="col-md-6 mb-2">
                            <button @click="editReconciliation(selectedReconciliation)" class="btn btn-warning w-100">
                              <i class="mdi mdi-pencil me-1"></i> Edit Reconciliation
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    <button v-if="canGenerateReport(selectedReconciliation)"
                            @click="generatePdfReport(selectedReconciliation)"
                            class="btn btn-primary">
                      <i class="mdi mdi-file-pdf-box me-1"></i> Generate Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
    </div>
  </HrPageLayout>
</template>


<script>
import * as bootstrap from 'bootstrap';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import moment from 'moment';
import { execute, authUrl } from '@/api';

export default {
  name: 'ImprestReconciliation',
  components: { HrPageLayout },
  data() {
    return {
      search: '',
      metrics: {
        totalImprestIssued: 0,
        pendingReconciliation: 0,
        totalOutstandingAmount: 0
      },
      reconciliations: [],
      unreconcyledRequests: [],
      newReconciliation: {
        imprest_id: '',
        actual_expense: null,
        details: '',
        attachments: []
      },

      selectedReconciliation: {
        request_id: "2",
        names: "Amos Kamiti",
        actual_expense: "2000",
        reconciliation_details: "KES 1000 Fuel\r\nKES 1000 Food",
        attachments: [
          "uploads/imprest_reconciliation/67d5624fc9401_receipt.pdf",
          "uploads/imprest_reconciliation/67d5624fc9503_receiptee.pdf"
        ],
        reconciled_by: "demo",
        reconciliation_date: "2025-03-15 14:19:43",
        status: "approved",
        requester_id: "demo",
        department_id: "DEP015",
        department: "HR",
        request_date: "2025-03-15 10:43:24",
        purpose: "Travel to Embu",
        amount: "3000.00",
        notes: "Meeting",
        approver_id: "demo",
        approval_date: "2025-03-15 13:34:13",
        rejection_reason: null,
        rejection_date: null,
        reconciled: "YES",
        reconciled_by_id: "demo"
      },
      
      uploadedFiles: [],
      loading: false,
      searchTimeout: null
    }
  },
  created() {
    this.getReconciliations();
    this.getUnreconciledRequests();
  },
  methods: {
    getPathUrl(path) {
      return authUrl+''+path;
    },
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },
    formatDate(dateString) {
      return moment(dateString).format('DD MMM YYYY');
    },
    getVarianceClass(item) {
      const amt = item.amount || item.imprest_amount || 0;
      const variance = parseFloat(amt) - parseFloat(item.actual_expense || 0);
      return variance > 0 ? 'hr-text-success' : variance < 0 ? 'hr-text-danger' : '';
    },
    getStatusBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return 'hr-badge hr-badge-warning';
      if (s === 'completed' || s === 'approved') return 'hr-badge hr-badge-success';
      if (s === 'rejected') return 'hr-badge hr-badge-danger';
      return 'hr-badge hr-badge-secondary';
    },
    getFileName(path) {
      if (!path) return '';
      // Extract the filename from the full path
      const parts = path.split('/');
      const filename = parts[parts.length - 1];

      // Extract the name portion after the underscore
      if (filename.includes('_')) {
        return filename.split('_').pop();
      }

      return filename;
    },
    showReconciliationModal() {
      const modalElement = document.getElementById('reconciliationModal');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    },
    handleSearch() {
      // Debounce search to avoid too many API calls
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getReconciliations();
      }, 500);
    },
    handleFileUpload(event) {
      this.uploadedFiles = event.target.files;
    },
    getReconciliations() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getImprestReconciliations");
      data.append("search", this.search);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.reconciliations = res.data.data.reconciliations;
              this.metrics = res.data.data.metrics;
            } else {
              this.reconciliations = [];
              // Don't popup for "no records" type messages
            }
          })
          .catch((e) => {
            this.reconciliations = [];
            console.error('getReconciliations:', e.message);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    getUnreconciledRequests() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getUnreconciledImprestRequests");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.unreconcyledRequests = res.data.data.unreconcyledRequests || [];
            } else {
              this.unreconcyledRequests = [];
              // Don't popup for "no imprest requests" / "no unreconciled" messages
            }
          })
          .catch((e) => {
            this.unreconcyledRequests = [];
            console.error('getUnreconciledRequests:', e.message);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    saveReconciliation() {
      if (!this.newReconciliation.imprest_id) {
        alert('Please select an imprest request');
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "saveImprestReconciliation");
      data.append("imprestId", this.newReconciliation.imprest_id);
      data.append("actualExpense", this.newReconciliation.actual_expense);
      data.append("details", this.newReconciliation.details);

      // Append files if any
      if (this.uploadedFiles.length > 0) {
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          data.append(`attachment_${i}`, this.uploadedFiles[i]);
        }
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Reset form
              this.newReconciliation = {
                imprest_id: '',
                actual_expense: null,
                details: '',
                attachments: []
              };
              this.uploadedFiles = [];

              // Close modal
              const modalElement = document.getElementById('reconciliationModal');
              if (modalElement) {
                const modalInstance = bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                  modalInstance.hide();
                }
              }

              // Refresh data
              this.getReconciliations();
              this.getUnreconciledRequests();

              // Show success message
              alert('Reconciliation submitted successfully');
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            alert(e.message);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    viewReconciliationDetails(item) {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getImprestReconciliationDetails");
      data.append("imprestId", item.request_id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedReconciliation = res.data.data.selectedReconciliation;

              // Show modal
              const modalElement = document.getElementById('detailsModal');
              if (modalElement) {
                const modal = new bootstrap.Modal(modalElement);
                modal.show();
              }
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            alert(e.message);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    submitReconciliation(item) {
      const confirmSubmit = confirm(`Are you sure you want to submit reconciliation for Imprest ID: ${item.request_id}?`);
      if (confirmSubmit) {
        this.loading = true;
        const data = new FormData();
        data.append("function", "submitImprestReconciliation");
        data.append("reconciliationId", item.request_id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                // Update the local item status
                const index = this.reconciliations.findIndex(rec => rec.id === item.request_id);
                if (index !== -1) {
                  this.reconciliations[index].status = 'Completed';
                }

                // Refresh metrics
                this.getReconciliations();

                alert('Reconciliation submitted successfully');
              } else {
                alert(res.data.message);
              }
            })
            .catch((e) => {
              alert(e.message);
            })
            .finally(() => {
              this.loading = false;
            });
      }
    },
    // Add these methods to your methods object in the Vue component

// Format date with time
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY, h:mm A');
    },

// Parse expense details from text (assuming format like "KES 1000 Fuel\r\nKES 1000 Food")
    parseExpenseDetails(detailsText) {
  if (!detailsText) return [];

  // Helper function to process a potential expense item
  function processExpenseItem(text) {
    text = text.trim();
    if (!text) return null;

    // Pattern 1: "KES 100 Fuel" format
    let match = text.match(/KES\s+(\d+(?:\.\d+)?)\s+(.*)/i);
    if (match) {
      return {
        amount: parseFloat(match[1]),
        description: match[2].trim()
      };
    }

    // Pattern 2: "Food 5000" format (description followed by amount)
    match = text.match(/([a-z][a-z\s]*)\s+(\d+(?:\.\d+)?)\s*$/i);
    if (match) {
      return {
        amount: parseFloat(match[2]),
        description: match[1].trim()
      };
    }

    // Pattern 3: "100 Housing" format (amount followed by description)
    match = text.match(/^(\d+(?:\.\d+)?)\s+([a-z][a-z\s]*)/i);
    if (match) {
      return {
        amount: parseFloat(match[1]),
        description: match[2].trim()
      };
    }

    return null;
  }

  // First, split by lines
  const lines = detailsText.split(/\r?\n/);
  const expenseItems = [];

  lines.forEach(line => {
    line = line.trim();
    if (!line) return;

    // Try processing as comma-separated items first
    const commaItems = line.split(/,\s*/);

    if (commaItems.length > 1) {
      // Line contains commas, process each part
      commaItems.forEach(item => {
        const expense = processExpenseItem(item);
        if (expense) expenseItems.push(expense);
      });
    } else {
      // No commas found, try to identify multiple expense items separated by spaces
      // Look for pattern: "100 food 500 transport" (alternating amount and description)
      const spaceMultipleItemPattern = /(\d+(?:\.\d+)?)\s+([a-z][a-z\s]*?)(?=\s+\d+(?:\.\d+)?\s+[a-z]|$)/gi;
      let multiMatch;
      let foundMultipleItems = false;

      while ((multiMatch = spaceMultipleItemPattern.exec(line)) !== null) {
        foundMultipleItems = true;
        expenseItems.push({
          amount: parseFloat(multiMatch[1]),
          description: multiMatch[2].trim()
        });
      }

      // If no multiple items found, try processing as a single item
      if (!foundMultipleItems) {
        const expense = processExpenseItem(line);
        if (expense) expenseItems.push(expense);
      }
    }
  });

  return expenseItems;
},

// Get percentage for expense breakdown visualization
    getPercentage(amount, total) {
      if (!total || total === 0) return 0;
      return Math.round((amount / total) * 100);
    },

// Get color classes for progress bars in expense breakdown
    getProgressBarColor(index) {
      const colors = [
        'bg-primary',
        'bg-success',
        'bg-info',
        'bg-warning',
        'bg-danger',
        'bg-secondary'
      ];
      return colors[index % colors.length];
    },

// Get text color for legend
    getTextColor(index) {
      const colors = [
        'text-primary',
        'text-success',
        'text-info',
        'text-warning',
        'text-danger',
        'text-secondary'
      ];
      return colors[index % colors.length];
    },

// Check if report generation is available
    canGenerateReport(reconciliation) {
      return reconciliation &&
          (reconciliation.status.toLowerCase() === 'completed' ||
              reconciliation.status.toLowerCase() === 'approved');
    },

// Generate PDF report
    generatePdfReport(reconciliation) {
      this.loading = true;

      try {
        // Import required libraries
        import('jspdf').then(({ default: jsPDF }) => {
          import('jspdf-autotable').then(({ default: autoTable }) => {
            // Create a new PDF document
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;

            // Add company logo if available
            // doc.addImage('/assets/images/logo.png', 'PNG', 15, 10, 50, 20);

            // Add title
            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.text('IMPREST RECONCILIATION REPORT', pageWidth / 2, 20, { align: 'center' });

            // Add report generation info
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated on: ${moment().format('DD MMM YYYY, h:mm A')}`, pageWidth - 15, 30, { align: 'right' });
            doc.text(`Report ID: IR-${reconciliation.request_id}-${moment().format('YYYYMMDD')}`, pageWidth - 15, 35, { align: 'right' });

            // Add imprest information table
            doc.setFontSize(12);
            doc.setTextColor(40, 40, 40);
            doc.text('Imprest Information', 15, 45);

            autoTable(doc, {
              startY: 50,
              head: [['Field', 'Value']],
              body: [
                ['Imprest ID', reconciliation.request_id],
                ['Purpose', reconciliation.purpose],
                ['Requested By', reconciliation.names],
                ['Department', reconciliation.department],
                ['Request Date', moment(reconciliation.request_date).format('DD MMM YYYY')],
                ['Approval Date', moment(reconciliation.approval_date).format('DD MMM YYYY')],
                ['Status', reconciliation.status.toUpperCase()],
                ['Notes', reconciliation.notes || 'N/A']
              ],
              theme: 'striped',
              headStyles: { fillColor: [66, 139, 202], textColor: 255 },
              columnStyles: {
                0: { cellWidth: 60, fontStyle: 'bold' },
                1: { cellWidth: 'auto' }
              },
              styles: { fontSize: 10, cellPadding: 5 }
            });

            // Add financial details table
            const currentY = doc.lastAutoTable.finalY + 15;
            doc.setFontSize(12);
            doc.text('Financial Details', 15, currentY);

            const variance = reconciliation.amount - reconciliation.actual_expense;
            const varianceStatus = variance > 0 ? 'Refund Due' : variance < 0 ? 'Reimbursement Required' : 'Balanced';

            autoTable(doc, {
              startY: currentY + 5,
              head: [['Description', 'Amount (KES)']],
              body: [
                ['Imprest Amount', this.formatCurrency(reconciliation.amount)],
                ['Actual Expense', this.formatCurrency(reconciliation.actual_expense)],
                ['Variance', this.formatCurrency(variance)],
                ['Status', varianceStatus]
              ],
              theme: 'grid',
              headStyles: { fillColor: [66, 139, 202], textColor: 255 },
              columnStyles: {
                0: { cellWidth: 100, fontStyle: 'bold' },
                1: { cellWidth: 'auto', halign: 'right' }
              },
              styles: { fontSize: 10, cellPadding: 5 },
              bodyStyles: {
                3: { fontStyle: 'bold' }
              },
              // Add special formatting for the variance row
              didDrawCell: (data) => {
                if (data.section === 'body' && data.row.index === 2) {
                  // Color the variance cell based on the value
                  if (variance > 0) {
                    doc.setFillColor(200, 250, 200); // Light green for positive
                  } else if (variance < 0) {
                    doc.setFillColor(250, 200, 200); // Light red for negative
                  }
                  doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                  doc.setTextColor(0);
                  doc.text(this.formatCurrency(variance), data.cell.x + data.cell.width - 5, data.cell.y + 10, { align: 'right' });
                }
              }
            });

            // Add expense details if available
            if (reconciliation.reconciliation_details) {
              const expenseY = doc.lastAutoTable.finalY + 15;

              // Check if we need a new page
              if (expenseY > 250) {
                doc.addPage();
                doc.setFontSize(12);
                doc.text('Expense Breakdown', 15, 20);

                // Parse expense details
                const expenseItems = this.parseExpenseDetails(reconciliation.reconciliation_details);

                if (expenseItems.length > 0) {
                  const expenseRows = expenseItems.map(item => [
                    item.description,
                    this.formatCurrency(item.amount),
                    `${this.getPercentage(item.amount, reconciliation.actual_expense)}%`
                  ]);

                  // Add total row
                  expenseRows.push([
                    'TOTAL',
                    this.formatCurrency(reconciliation.actual_expense),
                    '100%'
                  ]);

                  autoTable(doc, {
                    startY: 25,
                    head: [['Description', 'Amount (KES)', 'Percentage']],
                    body: expenseRows,
                    theme: 'striped',
                    headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                    columnStyles: {
                      0: { cellWidth: 'auto' },
                      1: { cellWidth: 60, halign: 'right' },
                      2: { cellWidth: 40, halign: 'right' }
                    },
                    styles: { fontSize: 10, cellPadding: 5 },
                    // Format the last row (total) with bold
                    didParseCell: (data) => {
                      if (data.section === 'body' && data.row.index === expenseRows.length - 1) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                      }
                    }
                  });
                } else {
                  // If we couldn't parse the expense details, just show the raw text
                  doc.setFontSize(10);
                  const details = reconciliation.reconciliation_details.split('\n');
                  let y = 25;
                  details.forEach(line => {
                    doc.text(line, 15, y);
                    y += 6;
                  });
                }
              } else {
                doc.setFontSize(12);
                doc.text('Expense Breakdown', 15, expenseY);

                // Parse expense details
                const expenseItems = this.parseExpenseDetails(reconciliation.reconciliation_details);

                if (expenseItems.length > 0) {
                  const expenseRows = expenseItems.map(item => [
                    item.description,
                    this.formatCurrency(item.amount),
                    `${this.getPercentage(item.amount, reconciliation.actual_expense)}%`
                  ]);

                  // Add total row
                  expenseRows.push([
                    'TOTAL',
                    this.formatCurrency(reconciliation.actual_expense),
                    '100%'
                  ]);

                  autoTable(doc, {
                    startY: expenseY + 5,
                    head: [['Description', 'Amount (KES)', 'Percentage']],
                    body: expenseRows,
                    theme: 'striped',
                    headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                    columnStyles: {
                      0: { cellWidth: 'auto' },
                      1: { cellWidth: 60, halign: 'right' },
                      2: { cellWidth: 40, halign: 'right' }
                    },
                    styles: { fontSize: 10, cellPadding: 5 },
                    // Format the last row (total) with bold
                    didParseCell: (data) => {
                      if (data.section === 'body' && data.row.index === expenseRows.length - 1) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                      }
                    }
                  });
                } else {
                  // If we couldn't parse the expense details, just show the raw text
                  doc.setFontSize(10);
                  const details = reconciliation.reconciliation_details.split('\n');
                  let y = expenseY + 5;
                  details.forEach(line => {
                    doc.text(line, 15, y);
                    y += 6;
                  });
                }
              }
            }

            // Add attachments list if available
            if (reconciliation.attachments && reconciliation.attachments.length > 0) {
              // Check if we need a new page
              if (doc.lastAutoTable && doc.lastAutoTable.finalY > 250) {
                doc.addPage();
                doc.setFontSize(12);
                doc.text('Supporting Documents', 15, 20);

                const attachmentRows = reconciliation.attachments.map(attachment => [
                  this.getFileName(attachment)
                ]);

                autoTable(doc, {
                  startY: 25,
                  head: [['Filename']],
                  body: attachmentRows,
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              } else {
                const attachmentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 200;

                doc.setFontSize(12);
                doc.text('Supporting Documents', 15, attachmentY);

                const attachmentRows = reconciliation.attachments.map(attachment => [
                  this.getFileName(attachment)
                ]);

                autoTable(doc, {
                  startY: attachmentY + 5,
                  head: [['Filename']],
                  body: attachmentRows,
                  theme: 'striped',
                  headStyles: { fillColor: [66, 139, 202], textColor: 255 },
                  styles: { fontSize: 10, cellPadding: 5 }
                });
              }
            }

            // Add footer to all pages
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
              doc.setPage(i);

              // Footer line
              doc.setDrawColor(200, 200, 200);
              doc.line(15, 280, pageWidth - 15, 280);

              // Footer text
              doc.setFontSize(8);
              doc.setTextColor(100, 100, 100);
              doc.text('Company Name - Imprest Reconciliation Report', 15, 285);
              doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, 285, { align: 'right' });
            }

            // Save the PDF
            const fileName = `Imprest_Reconciliation_${reconciliation.request_id}_${moment().format('YYYYMMDD')}.pdf`;
            doc.save(fileName);

            // Show success message
            this.$toast ? this.$toast.success('PDF report generated successfully') : alert('PDF report generated successfully');
          }).catch(error => {
            console.error('Error loading jspdf-autotable:', error);
            alert('Error generating PDF: Could not load required libraries');
          });
        }).catch(error => {
          console.error('Error loading jsPDF:', error);
          alert('Error generating PDF: Could not load required libraries');
        }).finally(() => {
          this.loading = false;
        });
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF: ' + error.message);
        this.loading = false;
      }
    },

// Helper function specifically for PDF report currency formatting
    formatCurrency(value) {
      if (value === null || value === undefined) return 'N/A';

      const formatter = new Intl.NumberFormat('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(value);
    },

// Edit reconciliation
    editReconciliation(reconciliation) {
      // This would be implemented to allow editing the reconciliation
      // Typically you would populate the newReconciliation object and show the form modal
      this.newReconciliation = {
        imprest_id: reconciliation.request_id,
        actual_expense: reconciliation.actual_expense,
        details: reconciliation.reconciliation_details,
        attachments: []
      };

      // Hide details modal
      const detailsModalElement = document.getElementById('detailsModal');
      if (detailsModalElement) {
        const modalInstance = bootstrap.Modal.getInstance(detailsModalElement);
        if (modalInstance) {
          modalInstance.hide();
        }
      }

      // Show reconciliation modal
      setTimeout(() => {
        this.showReconciliationModal();
      }, 500);
    }
  }
}
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-search-wrap { position: relative; }
.hr-search-wrap .hr-input { padding-right: 36px; }
.hr-search-wrap .search-icon { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.hr-btn-group { display: flex; gap: 8px; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(5,150,105,0.2); border-top-color: #059669; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>

